'use client';

/**
 * ADS DECISION DECK (Boss 2026-07-10: "one page is too much information —
 * I need focus cards, properly grouped, pushing for making the decision").
 * The dashboard wall (AdsParity) survives at 'ads-full'; THIS is the front door.
 *
 * Cards = decisions, from the SAME real APIs as the parity room:
 *   GET /api/ads/accounts, GET /api/ads/audit/[id]?window=last_30d,
 *   GET /api/ads/insights/[id]?window=last_30d
 *   card 0 = STATE (orientation, no decision)
 *   then RECOMMENDATIONS (priority) → FINDINGS (severity) → INSIGHTS.
 * Groups (filter pills) = BUDGET · AUDIENCE · DELIVERY · CREATIVE · SETUP,
 * keyword-derived. Decisions persist in localStorage bcc.ads.deck.decisions.<acct>;
 * decided cards leave the queue. DELEGATE fires the fix into the main Beast
 * session (POST /api/gw/chat — same stream as the orb/chat panel).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OneFocusDeck, bigVerb, ghostVerb, CY, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';
import type { Audit, InsightsBundle } from '@/lib/ads-types';
import type { BusinessProject } from '@/lib/business-projects';

const WINDOW = 'last_30d';
const money = (n: number, cur = 'CAD') => `${cur === 'CAD' ? 'CA$' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : cur + ' '}${n >= 10000 ? `${Math.round(n / 100) / 10}K` : n.toFixed(2)}`;
const pct = (v?: number) => (v == null || !isFinite(v) ? '—' : `${v > 0 ? '↑' : '↓'}${Math.abs(v).toFixed(0)}%`);

/* group derivation — keyword buckets over title+detail */
function groupOf(text: string): string {
  const t = text.toLowerCase();
  if (/(budget|spend|cpc|cost|scal|bid)/.test(t)) return 'budget';
  if (/(male|female|gender|age|audience|demograph|country|geo)/.test(t)) return 'audience';
  if (/(placement|platform|device|frequency|delivery|reach|impression)/.test(t)) return 'delivery';
  if (/(creative|ctr|fatigue|copy|image|video|ad\b)/.test(t)) return 'creative';
  return 'setup';
}

type Decision = 'accepted' | 'dismissed' | 'delegated';
const decKey = (acct: string) => `bcc.ads.deck.decisions.${acct}`;
const getDecisions = (acct: string): Record<string, Decision> => {
  try { return JSON.parse(localStorage.getItem(decKey(acct)) || '{}'); } catch { return {}; }
};
const setDecision = (acct: string, id: string, d: Decision) => {
  const all = getDecisions(acct); all[id] = d;
  try { localStorage.setItem(decKey(acct), JSON.stringify(all)); } catch { }
};

interface Ctx { audit: Audit | null; bundle: InsightsBundle | null; acct: string; cur: string; openFull: () => void }
let CTX: Ctx = { audit: null, bundle: null, acct: '', cur: 'CAD', openFull: () => { } };

/* ── WHY — only the numbers proving THIS card's point ─────────────────── */
function Why({ item }: { item: OFItem }) {
  const a = CTX.audit; const r = item.raw;
  const rows: [string, string, string?][] = [];
  if (item.type === 'STATE') {
    a?.scoreParts?.forEach(p => rows.push([p.label.toUpperCase(), `${p.score}`, p.note]));
  } else {
    const t = a?.trend || {};
    const rel = `${r.title} ${r.detail}`.toLowerCase();
    const metricRows: [string, string][] = [
      ['SPEND', pct(t.spend)], ['CTR', pct(t.ctr)], ['CPC', pct(t.cpc)], ['CPM', pct(t.cpm)],
      ['CLICKS', pct(t.clicks)], ['REACH', pct(t.reach)],
    ];
    metricRows.filter(([k]) => rel.includes(k.toLowerCase()) || ['SPEND', 'CTR', 'CPC'].includes(k))
      .forEach(([k, v]) => rows.push([k, v, 'VS PRIOR']));
    if (r.entity) rows.push(['ENTITY', String(r.entity)]);
  }
  return (
    <>
      {rows.map(([k, v, n], i) => (
        <div key={i} className="flex justify-between items-baseline" style={{ padding: '5px 0', gap: 10 }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd', flexShrink: 0 }}>{k}</span>
          <span style={{ textAlign: 'right' }}>
            <span style={{ ...mono(18, '.04em'), color: '#d7f2ff' }}>{v}</span>
            {n && <span style={{ ...mono(12, '.1em'), color: '#49677a', display: 'block' }}>{n}</span>}
          </span>
        </div>
      ))}
      <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
        <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 8 }}>{item.type === 'STATE' ? 'CAVEATS' : 'DETAIL'}</div>
        <div style={{ ...display(23, 300), color: 'var(--uif-t-body)', lineHeight: 1.6 }}>
          {item.type === 'STATE' ? (a?.notes?.join(' ') || 'No caveats.') : r.detail}
        </div>
      </div>
    </>
  );
}

/* ── PREVIEW — the ONE breakdown/chart for this decision ──────────────── */
function Bars({ rows, cur }: { rows: { key: string; spend: number; ctr: number; cpc: number }[]; cur: string }) {
  const max = Math.max(...rows.map(r => r.spend), 1);
  return (
    <div>
      {rows.slice(0, 8).map(r => (
        <div key={r.key} style={{ marginBottom: 12 }}>
          <div className="flex justify-between" style={{ marginBottom: 3 }}>
            <span style={{ ...mono(15, '.14em'), color: '#d7f2ff' }}>{r.key.toUpperCase()}</span>
            <span style={{ ...mono(14), color: '#7fa9bd' }}>{money(r.spend, cur)} · CTR {r.ctr?.toFixed(2)}% · CPC {money(r.cpc || 0, cur)}</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: 'rgba(125,232,255,.08)' }}>
            <div style={{ height: '100%', width: `${(r.spend / max) * 100}%`, borderRadius: 4, background: `linear-gradient(90deg,#5fd4ef,${CY})`, boxShadow: '0 0 10px rgba(0,240,255,.35)' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Spark({ pts, label }: { pts: number[]; label: string }) {
  if (pts.length < 2) return null;
  const min = Math.min(...pts), max = Math.max(...pts), sp = max - min || 1;
  const d = pts.map((v, i) => `${i ? 'L' : 'M'}${(i / (pts.length - 1)) * 560},${64 - ((v - min) / sp) * 56}`).join(' ');
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ ...mono(14, '.22em'), color: '#49677a', marginBottom: 6 }}>{label}</div>
      <svg width="100%" viewBox="0 0 560 68" style={{ display: 'block' }}>
        <path d={d} fill="none" stroke={CY} strokeWidth="2" style={{ filter: 'drop-shadow(0 0 6px rgba(0,240,255,.5))' }} />
      </svg>
    </div>
  );
}

function Preview({ item }: { item: OFItem }) {
  const b = CTX.bundle; const a = CTX.audit; const cur = CTX.cur;
  const g = item.biz;
  if (b && g === 'audience' && (b.byGender?.length || b.byAge?.length)) {
    return (
      <div>
        {b.byGender?.length > 0 && <><div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>BY GENDER · SPEND SHARE</div><Bars rows={b.byGender as any} cur={cur} /></>}
        {b.byAge?.length > 0 && <><div style={{ ...mono(14, '.26em'), color: '#49677a', margin: '14px 0 10px' }}>BY AGE</div><Bars rows={b.byAge as any} cur={cur} /></>}
      </div>
    );
  }
  if (b && g === 'delivery' && (b.byPlatform?.length || b.byDevice?.length)) {
    return (
      <div>
        {b.byPlatform?.length > 0 && <><div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>BY PLATFORM</div><Bars rows={b.byPlatform as any} cur={cur} /></>}
        {b.byDevice?.length > 0 && <><div style={{ ...mono(14, '.26em'), color: '#49677a', margin: '14px 0 10px' }}>BY DEVICE</div><Bars rows={b.byDevice as any} cur={cur} /></>}
      </div>
    );
  }
  /* default: spend + CTR over time, then benchmarks */
  const ts = b?.timeseries || [];
  return (
    <div>
      {ts.length > 1 && <><Spark pts={ts.map(p => p.spend)} label="SPEND · DAILY" /><Spark pts={ts.map(p => p.ctr)} label="CTR % · DAILY" /></>}
      {a?.benchmarks && a.benchmarks.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>VS REFERENCE</div>
          {a.benchmarks.map(bm => (
            <div key={bm.metric} className="flex justify-between" style={{ padding: '5px 0' }}>
              <span style={{ ...mono(15, '.16em'), color: '#7fa9bd' }}>{bm.metric.toUpperCase()}</span>
              <span style={{ ...mono(15) }}>
                <span style={{ color: bm.verdict === 'inline' ? '#d7f2ff' : (bm.verdict === 'above') === (bm.better === 'higher') ? '#5df2b4' : '#ff7d8a' }}>{bm.you}{bm.unit}</span>
                <span style={{ color: '#49677a' }}> · REF {bm.reference}{bm.unit}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── DECIDE — accept / dismiss / delegate to Beast ─────────────────────── */
function Decide({ item, refresh, advance, speak }: { item: OFItem; refresh: () => void; advance: () => void; speak: (t: string) => void }) {
  const [sent, setSent] = useState(false);
  if (item.type === 'STATE') {
    return (
      <>
        <div style={{ ...display(23, 300), color: 'var(--uif-t-body)', lineHeight: 1.6, marginBottom: 16 }}>
          Orientation card — step right to start deciding. The full dashboard is one tap away.
        </div>
        <button onMouseUp={CTX.openFull} style={bigVerb('#5fd4ef', 'rgba(0,240,255,.4)')}>◈ FULL BOARD</button>
      </>
    );
  }
  const decide = (d: Decision, say: string) => {
    setDecision(CTX.acct, item.id, d);
    speak(say); refresh(); advance();
  };
  const delegate = () => {
    setSent(true);
    fetch('/api/gw/chat', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: `Ads decision deck — Boss delegated this ${item.type?.toLowerCase()} for account ${CTX.audit?.account?.name || CTX.acct}: "${item.raw.title}" — ${item.raw.detail}. Execute the fix or prepare it for review.` }),
    }).catch(() => { });
    decide('delegated', 'Delegated to Beast.');
  };
  return (
    <>
      <button onMouseUp={() => decide('accepted', 'Accepted.')} style={bigVerb()}>✓ ACCEPT</button>
      <button onMouseUp={delegate} disabled={sent}
        style={{ width: '100%', padding: '14px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: `1px solid rgba(125,232,255,.35)`, color: CY, ...mono(17, '.24em'), cursor: 'pointer', opacity: sent ? .5 : 1 }}>
        ⚡ DELEGATE TO BEAST
      </button>
      <button onMouseUp={() => decide('dismissed', 'Dismissed.')} style={ghostVerb}>✕ DISMISS</button>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 12, textAlign: 'center' }}>DECIDED CARDS LEAVE THE QUEUE</div>
    </>
  );
}

/* ── surface ───────────────────────────────────────────────────────────── */
export function AdsDeckSurface({ project, openFull }: { project?: BusinessProject | null; openFull: () => void }) {
  const [acct, setAcct] = useState('');
  const [audit, setAudit] = useState<Audit | null>(null);
  const [bundle, setBundle] = useState<InsightsBundle | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0); // decisions bump → rebuild items

  useEffect(() => {
    if (project?.adsAccountId) { setAcct(project.adsAccountId); return; }
    fetch('/api/ads/accounts').then(r => r.json())
      .then(d => { const rows = d.accounts || d || []; if (rows[0]?.id) setAcct(rows[0].id); else setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [project?.adsAccountId]);

  useEffect(() => {
    if (!acct) return;
    let live = true;
    fetch(`/api/ads/audit/${acct}?window=${WINDOW}`).then(r => r.json())
      .then(d => { if (live) { setAudit(d?.audit || d); setLoaded(true); } }).catch(() => setLoaded(true));
    fetch(`/api/ads/insights/${acct}?window=${WINDOW}`).then(r => r.json())
      .then(d => { if (live) setBundle(d?.bundle || d); }).catch(() => { });
    return () => { live = false; };
  }, [acct]);

  const refresh = useCallback(() => setTick(t => t + 1), []);
  CTX = { audit, bundle, acct, cur: audit?.account?.currency || 'CAD', openFull };

  const items = useMemo<OFItem[]>(() => {
    if (!audit || (audit as any).error) return [];
    const cur = audit.account?.currency || 'CAD';
    const decided = acct ? getDecisions(acct) : {};
    const cards: OFItem[] = [{
      id: 'state', biz: 'state', title: audit.account?.name || 'ACCOUNT', type: 'STATE', status: 'info',
      kpis: [{ l: 'SCORE', v: `${audit.score}` }, { l: 'SPEND', v: money(audit.totals?.spend || 0, cur) }, { l: 'CTR', v: `${(audit.totals?.ctr || 0).toFixed(2)}%` }],
      taste: `Last 30 days vs prior: spend ${pct(audit.trend?.spend)}, CTR ${pct(audit.trend?.ctr)}, CPC ${pct(audit.trend?.cpc)}.`,
      foot: `AUDIT ${audit.generatedAt?.slice(0, 16).replace('T', ' ') || ''} · ${audit.source?.toUpperCase() || ''}`, raw: audit,
    }];
    const pri = { high: 0, medium: 1, low: 2 } as Record<string, number>;
    const sev = { critical: 0, warning: 1, info: 2, positive: 3 } as Record<string, number>;
    [...(audit.recommendations || [])].sort((a, b) => (pri[a.priority] ?? 9) - (pri[b.priority] ?? 9)).forEach((r, i) => cards.push({
      id: `rec-${i}-${r.title.slice(0, 24)}`, biz: groupOf(`${r.title} ${r.detail}`), title: r.title, type: 'RECOMMENDATION',
      status: r.priority, kpis: [{ l: 'PRIORITY', v: r.priority.toUpperCase() }], taste: r.detail.slice(0, 220), raw: r,
    }));
    [...(audit.findings || [])].filter(f => f.severity !== 'positive').sort((a, b) => (sev[a.severity] ?? 9) - (sev[b.severity] ?? 9)).forEach((f, i) => cards.push({
      id: `fnd-${i}-${f.title.slice(0, 24)}`, biz: groupOf(`${f.title} ${f.detail}`), title: f.title, type: 'FINDING',
      status: f.severity, kpis: [{ l: 'SEVERITY', v: f.severity.toUpperCase() }, ...(f.entity ? [{ l: 'ENTITY', v: String(f.entity).slice(0, 12).toUpperCase() }] : [])],
      taste: f.detail.slice(0, 220), raw: f,
    }));
    (bundle?.insights || []).forEach((s, i) => cards.push({
      id: `ins-${i}-${s.title.slice(0, 24)}`, biz: groupOf(`${s.title} ${s.detail}`), title: s.title, type: 'INSIGHT',
      status: s.severity, kpis: [{ l: 'SOURCE', v: 'BREAKDOWN' }], taste: s.detail.slice(0, 220), raw: s,
    }));
    return cards.filter(c => !decided[c.id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit, bundle, acct, tick]);

  const adapter: OFAdapter = {
    kind: 'ads',
    strap: (its) => {
      const n = its.filter(i => i.type !== 'STATE').length;
      return `ADS · ${n} TO DECIDE · SCORE ${audit?.score ?? '—'}`; // short — strap shares the bottom row with status chips
    },
    decidable: (it) => it.type !== 'STATE',
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={refresh} />;
}
